import React from "react";

import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import CarouselComponentCustomize from "./CarouselComponentCustomize";
import ConditionalRenderEditor from "./components/ConditionalRenderEditor";
import FormatSelector from "./components/FormatSelector";
import Slider from "./components/Slider";
import SwitchOnOff from "../../components/common/tools/SwitchOnOff";
import ComponentDisplayOrder from "../../components/platform/layout/ComponentDisplayOrder";
import CarouselButtonPosition from "../../components/utils/CarouselButtonPosition";
import SegmentedControl from "../../components/utils/SegmentedControl/SegmentedControl";
import SmartlistDataSource from "../../components/utils/SmartlistDataSource";
import { COMPONENT_TYPES } from "../../constants/componentType";
import {
  ADDITIONNAL_IMAGE_FORMATS,
  DEFAULT_IMAGE_FORMATS,
} from "../../constants/ratio";
import {
  MOST_VIEWED_TAB,
  MY_LIST_TAB,
  NOW_TAB,
  PURCHASE_TAB,
  RESUME_TAB,
  SMARTLIST_TAB,
  USER_RECOMMENDATION_TAB,
} from "../../types/platform";
import { isCarouselComponent } from "../../utils/typePredicate";

import type { JSX } from "react";

import type {
  CarouselComponent,
  ComponentWithUnknownDataSourceParams,
  ConditionRender,
  DisplayOrder,
  MosaicComponentWithoutIndividualCarousel,
} from "../../api/ProjectAPI.types";
import type { SegmentedControlTab } from "../../components/utils/SegmentedControl/SegmentedControl";
import type { AppState } from "../../reducers";

const { MOSAIC, CAROUSEL } = COMPONENT_TYPES;

const HEADER_POSITION_TABS: SegmentedControlTab<"left" | "center" | "right">[] =
  [
    { label: "left", value: "left" },
    { label: "center", value: "center" },
    { label: "right", value: "right" },
  ];

interface CarouselOrMosaicFormProps {
  component:
    | CarouselComponent
    | MosaicComponentWithoutIndividualCarousel
    | undefined;
  onComponentUpdate: (
    updatedComponent:
      | CarouselComponent
      | MosaicComponentWithoutIndividualCarousel,
  ) => void;
}
export default function CarouselOrMosaicForm({
  component,
  onComponentUpdate,
}: CarouselOrMosaicFormProps): JSX.Element {
  const { t } = useTranslation("editor-platform");

  const playlistType = useSelector(
    (state: AppState) =>
      state.architectureReducer.project?.storefront_project_params
        ?.playlists_type,
  );

  if (!component) return <div>Error</div>;

  const isSinglePlaylistProject = playlistType === "single";

  const tabs = [
    SMARTLIST_TAB,
    ...(isSinglePlaylistProject ? [MY_LIST_TAB] : []),
    RESUME_TAB,
    USER_RECOMMENDATION_TAB,
    PURCHASE_TAB,
    NOW_TAB,
    MOST_VIEWED_TAB,
  ];

  const isNowDataSourceType =
    component.static_component?.smartlist_module_name === "now";
  const isMostViewedDataSourceType =
    component.static_component?.smartlist_module_name === "mostViewed";
  const isSmartlistDataSourceType = component.dynamic_component !== null;

  const isCarousel = isCarouselComponent(component);

  const imageFormatList = isCarousel
    ? [...DEFAULT_IMAGE_FORMATS, ...ADDITIONNAL_IMAGE_FORMATS]
    : DEFAULT_IMAGE_FORMATS;

  const carouselRow = isCarousel ? component.params.row : undefined;

  const isTypeSelectedClassNames = (source: "mosaic" | "carousel"): string =>
    classNames("editor-component-type", {
      selected: component.type === source,
    });

  const handleChangeComponentType = (
    updatedType: "mosaic" | "carousel",
  ): void => {
    if (component.type === updatedType) return;

    const baseComponent = {
      label: component.label,
      section: component.section,
      platform: component.platform,
      type: updatedType,
      enabled: component.enabled,
      dynamic_component: component.dynamic_component,
      static_component: component.static_component,
      ...("uuid" in component && component.uuid
        ? { uuid: component.uuid }
        : {}),
      ...("rank" in component && component.rank
        ? { rank: component.rank }
        : {}),
      params: {
        display_item_title: component.params.display_item_title,
        display_title: component.params.display_title,
        headerPosition: component.params.headerPosition,
        display_description: component.params.display_description,
        has_conditions: component.params?.has_conditions,
        conditional_render: component.params?.conditional_render,
        ratio: component.params?.ratio,
        display_dialog: component.params.display_dialog,
        display_order: component.params.display_order,
        ...("period" in component.params && component.params.period
          ? { period: component.params.period }
          : {}),
        ...("maxItems" in component.params && component.params.maxItems
          ? { maxItems: component.params.maxItems }
          : {}),
        ...("hasNumberOnItems" in component.params &&
        component.params.hasNumberOnItems
          ? { hasNumberOnItems: component.params.hasNumberOnItems }
          : {}),
      },
    };

    if (updatedType === "mosaic") {
      onComponentUpdate(
        baseComponent as MosaicComponentWithoutIndividualCarousel,
      );
    } else {
      const updatedComponent = {
        ...baseComponent,
        params: {
          ...baseComponent.params,
          arrows_on_hover: true,
          circular: true,
          component_per_slides: 6,
          display_arrows: true,
        },
      };
      onComponentUpdate(updatedComponent as CarouselComponent);
    }
  };

  const handleChangeParamsOption = (
    options: Partial<
      | CarouselComponent["params"]
      | MosaicComponentWithoutIndividualCarousel["params"]
    >,
  ): void => {
    const updatedComponent = {
      ...component,
      params: { ...component.params, ...options },
    };

    onComponentUpdate(
      updatedComponent as
        | CarouselComponent
        | MosaicComponentWithoutIndividualCarousel,
    );
  };

  const handleChangeConditionalRender = (
    conditions: ConditionRender[],
  ): void => {
    const updatedComponent = {
      ...component,
      params: { ...component.params, conditional_render: conditions },
    };

    onComponentUpdate(
      updatedComponent as
        | CarouselComponent
        | MosaicComponentWithoutIndividualCarousel,
    );
  };

  const handleChangeInterval = (value: number): void => {
    if (value <= 0 || value > 20000) {
      const correctedValue = value < 1 ? 1000 : 20000;

      const updatedComponent = {
        ...component,
        params: { ...component.params, interval: correctedValue },
      };
      onComponentUpdate(
        updatedComponent as
          | CarouselComponent
          | MosaicComponentWithoutIndividualCarousel,
      );
    }
  };

  const handleChangeDisplayButtonPosition = (value: boolean): void => {
    handleChangeParamsOption({
      display_button: value,
      button_horizontal_position: "left",
      button_vertical_position: "bottom",
    });
  };

  return (
    <React.Fragment>
      <div className="editor-form-section section-separator">
        <div className="editor-form-row">
          <div className="editor-form-section-title">{t("componentType")}</div>
          <div className="editor-form-input">
            <div
              className={isTypeSelectedClassNames(CAROUSEL)}
              onClick={(): void => handleChangeComponentType(CAROUSEL)}
            >
              <div className="editor-component-type-icon">
                <i className="material-icons">view_carousel</i>
              </div>
              <span>{t("carousel")}</span>
            </div>
            <div
              className={isTypeSelectedClassNames(MOSAIC)}
              onClick={(): void => handleChangeComponentType(MOSAIC)}
            >
              <div className="editor-component-type-icon">
                <i className="material-icons">view_module</i>
              </div>
              <span>{t("mosaic")}</span>
            </div>
          </div>
        </div>
        <FormatSelector
          carouselRow={carouselRow}
          imageFormatList={imageFormatList}
          isCarousel={isCarousel}
          onChange={handleChangeParamsOption}
          ratio={component.params.ratio}
        />{" "}
        {isCarousel ? (
          <CarouselComponentCustomize
            component={component}
            onChange={(option: string, value: string | boolean): void =>
              handleChangeParamsOption({ [option]: value })
            }
          />
        ) : null}
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              checked={component.params.display_title}
              iconName=""
              name="display_title"
              size="sm"
              onChange={(value: boolean): void => {
                handleChangeParamsOption({ display_title: value });
              }}
            />
          </div>
          <div className="editor-form-label">{t("showComponentTitle")}</div>
        </div>
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              checked={component.params.display_description}
              disabled={!isSmartlistDataSourceType}
              iconName=""
              name="display_description"
              size="sm"
              onChange={(value: boolean): void => {
                handleChangeParamsOption({ display_description: value });
              }}
            />
          </div>
          <div className="editor-form-label">
            {t("showComponentShortDescription")}
          </div>
        </div>
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              checked={component.params.display_castings}
              disabled={!isSmartlistDataSourceType}
              iconName=""
              name="display_castings"
              size="sm"
              onChange={(value: boolean): void => {
                handleChangeParamsOption({ display_castings: value });
              }}
            />
          </div>
          <div className="editor-form-label">{t("showCurator")}</div>
        </div>
        {component.params.display_title ||
        component.params.display_description ? (
          <div className="mb-2.5 flex flex-col">
            <div className="p-1 font-medium">{t("headerPosition")}</div>
            <SegmentedControl
              selected={component.params.headerPosition ?? "left"}
              tabs={HEADER_POSITION_TABS}
              onChange={(value): void => {
                handleChangeParamsOption({ headerPosition: value });
              }}
            />
          </div>
        ) : null}
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              checked={component.params.display_item_title}
              iconName=""
              name="display_item_title"
              size="sm"
              onChange={(value: boolean): void => {
                handleChangeParamsOption({ display_item_title: value });
              }}
            />
          </div>
          <div className="editor-form-label">{t("showItemsTitle")}</div>
        </div>
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              checked={component.params.display_dialog}
              disabled={isNowDataSourceType}
              iconName=""
              name="display_dialog"
              size="sm"
              onChange={(value: boolean): void => {
                handleChangeParamsOption({ display_dialog: value });
              }}
            />
          </div>
          <div className="editor-form-label">{t("showDialog")}</div>
        </div>
        {isCarousel ? (
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={component.params.circular}
                iconName=""
                name="circular"
                size="sm"
                onChange={(value: boolean): void => {
                  handleChangeParamsOption({ circular: value });
                }}
              />
            </div>
            <div className="editor-form-label">{t("showCircular")}</div>
          </div>
        ) : null}
        {isCarousel && component.params.circular ? (
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={component.params.autoplay}
                iconName=""
                name="autoplay"
                size="sm"
                onChange={(value: boolean): void => {
                  handleChangeParamsOption({ autoplay: value });
                }}
              />
            </div>
            <div className="editor-form-label">{t("autoplay")}</div>
          </div>
        ) : null}
        {isCarousel &&
        component.params.circular &&
        component.params.autoplay ? (
          <div className="editor-form-row editor-form-row-inline editor-form-interval">
            <TextField
              id="interval"
              size="small"
              type="number"
              variant="outlined"
              InputProps={{
                endAdornment: <InputAdornment position="end">s</InputAdornment>,
                inputProps: { min: 1, max: 20, step: 1 },
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>): void => {
                handleChangeInterval(Number(event.target.value) * 1000);
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                handleChangeParamsOption({
                  interval: Number(event.target.value) * 1000,
                });
              }}
              value={
                component.params.interval === 0
                  ? ""
                  : (component.params.interval ?? 0) / 1000
              }
            />
            <div className="editor-form-label">{t("interval")}</div>
          </div>
        ) : null}
        {isCarousel ? (
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={component.params.display_arrows}
                iconName=""
                name="display_arrows"
                size="sm"
                onChange={(value: boolean): void => {
                  handleChangeParamsOption({ display_arrows: value });
                }}
              />
            </div>
            <div className="editor-form-label">{t("showArrow")}</div>
          </div>
        ) : null}
        {isCarousel ? (
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={component.params.arrows_on_hover}
                iconName=""
                name="arrows_on_hover"
                size="sm"
                onChange={(value: boolean): void => {
                  handleChangeParamsOption({ arrows_on_hover: value });
                }}
              />
            </div>
            <div className="editor-form-label">{t("showArrowHover")}</div>
          </div>
        ) : null}
        <ComponentDisplayOrder
          data={component.params.display_order}
          disabled={isNowDataSourceType || isMostViewedDataSourceType}
          hasPublicationDateOrder={isSmartlistDataSourceType}
          onChange={(displayOrder: DisplayOrder): void =>
            handleChangeParamsOption({ display_order: displayOrder })
          }
        />
        {isCarousel ? (
          <div className="editor-form-row m-t-10">
            <div className="editor-form-label">{t("componentPerSlides")}</div>
            <div className="editor-form-input">
              <Slider
                max={10}
                min={1}
                step={1}
                value={component.params.component_per_slides || 6}
                onChange={(value: number): void => {
                  if (value !== 1)
                    handleChangeParamsOption({ display_button: false });
                  handleChangeParamsOption({ component_per_slides: value });
                }}
              />
            </div>
          </div>
        ) : null}
        {isCarousel && component.params.component_per_slides === 1 ? (
          <div>
            <div
              key="switch"
              className="editor-form-row editor-form-row-inline"
            >
              <div className="editor-form-input">
                <SwitchOnOff
                  checked={component.params.display_button}
                  iconName=""
                  name="display_button"
                  onChange={handleChangeDisplayButtonPosition}
                  size="sm"
                />
              </div>
              <div className="editor-form-label">{t("showButton")}</div>
            </div>
            {!!component.params.display_button && (
              <div className="editor-form-row editor-form-row-half-size">
                <div className="editor-form-label">{t("buttonPosition")}</div>
                <div className="editor-form-input">
                  <CarouselButtonPosition
                    ratio={component.params.ratio}
                    buttonHorizontalPosition={
                      component.params.button_horizontal_position
                    }
                    buttonVerticalPosition={
                      component.params.button_vertical_position
                    }
                    onPositionChange={(
                      horizontal: "left" | "center" | "right",
                      vertical: "center" | "top" | "bottom",
                    ): void => {
                      handleChangeParamsOption({
                        button_horizontal_position: horizontal,
                        button_vertical_position: vertical,
                      });
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
      <div className="editor-form-section section-separator">
        <SmartlistDataSource
          item={component as ComponentWithUnknownDataSourceParams}
          label={t("datasourceType")}
          tabs={tabs}
          onComponentUpdate={
            onComponentUpdate as (
              item: ComponentWithUnknownDataSourceParams,
            ) => void
          }
        />
      </div>
      <ConditionalRenderEditor
        componentParams={component.params}
        onChangeConditions={handleChangeConditionalRender}
        onChangeConditionsToggle={(value: boolean): void => {
          handleChangeParamsOption({ has_conditions: value });
        }}
      />
    </React.Fragment>
  );
}
